/**
 * M3 FLIGHT-FEEL A6: client-only "juice" — dynamic FOV + deterministic camera
 * shake that sell speed and boost WITHOUT touching sim-core. There is NO physics
 * here: this only reads scalar flight metrics (speed01, thrustFrac, boost01) and
 * a deterministic clock (simTime) and produces a camera FOV + a tiny shake offset.
 *
 * DETERMINISM: shake uses value-noise built from sums of sines of `simTime`
 * (never Math.random / Date.now), so two runs with the same simTime stream shake
 * identically — the HARD invariant for reproducible client behaviour.
 */
import * as THREE from 'three';
import { createFlightFeelTuning, type FlightFeelTuning } from '../feelTuning.js';

const BASE_FOV = 60; // matches scene.ts PerspectiveCamera base
/** Extra FOV pushed in during supercruise — distinct mode cue, suppresses the normal collapse. */
const CRUISE_FOV = 10; // +deg at full cruise intensity (smoothly widened on engage)
const MAX_FOV = 82; // hard cap (avoid fish-eye nausea)
const FOV_LERP = 6; // smoothing rate (1/s) toward target FOV
const FOV_EPS = 0.01; // skip updateProjectionMatrix below this delta

// AAA-17 (audit session 6): HARD ceiling on the position nudge. At peak amp
// (thrust+boost+speed ≈ 0.0047) the old scale-12 worst case reached ~0.098 km —
// past the 0.05 km floating-origin comfort ceiling. Scale 9 + this clamp keep
// every frame's nudge provably under it.
const SHAKE_POS_MAX_KM = 0.05;

/** A small camera offset the rig applies: a tiny position + rotation jitter. */
export interface ShakeOffset {
  pos: THREE.Vector3; // km, ship-relative
  pitch: number; // rad
  yaw: number; // rad
  roll: number; // rad
}

/** Deterministic value-noise in [-1,1] from time: a sum of incommensurate sines. */
const noise = (t: number, f1: number, f2: number): number =>
  (Math.sin(t * f1) * 0.6 + Math.sin(t * f2 + 1.3) * 0.4);

const clamp01 = (v: number): number => Math.max(0, Math.min(1, v));

export class FlightFeel {
  readonly tuning: FlightFeelTuning = createFlightFeelTuning();
  private fov = BASE_FOV;
  /** 0..1 supercruise intensity used to smooth the cruise FOV widen. */
  private cruiseIntensity = 0;
  private impulse = 0;
  readonly shake: ShakeOffset = {
    pos: new THREE.Vector3(),
    pitch: 0,
    yaw: 0,
    roll: 0,
  };

  /**
   * Advance the feel state.
   * @param dt        wall-frame seconds
   * @param speed01   0..1 current speed / max speed
   * @param thrustFrac 0..1 commanded thrust magnitude
   * @param boost01   0..1 boost remaining
   * @param simTime   deterministic sim clock (seconds) — drives shake noise
   * @param suppress  when true (supercruise) collapse shake to calm; FOV handled by setCruiseIntensity
   */
  update(
    dt: number,
    speed01: number,
    thrustFrac: number,
    boost01: number,
    simTime: number,
    suppress = false,
  ): void {
    if (suppress) this.impulse = 0;
    else this.impulse = Math.max(0, this.impulse - dt / 0.3);
    // Normal flight: speed+boost push FOV up. Cruise: let setCruiseIntensity drive the widen.
    const target = suppress
      ? BASE_FOV + CRUISE_FOV * this.cruiseIntensity
      : Math.min(
        MAX_FOV,
        BASE_FOV + this.tuning.speedFov * speed01 + this.tuning.boostFov * boost01,
      );
    this.fov += (target - this.fov) * Math.min(1, FOV_LERP * dt);
    this.updateShake(suppress ? 0 : thrustFrac, suppress ? 0 : speed01, suppress ? 0 : boost01, simTime);
  }

  addImpulse(v: number): void {
    this.impulse = Math.max(this.impulse, clamp01(v));
  }

  /**
   * Set the supercruise warp intensity (0..1) so the FOV widen tracks it.
   * Called each frame from main.ts alongside the warp overlay update.
   * On disengage (intensity→0) the widen collapses back to BASE_FOV smoothly
   * via the existing FOV lerp in update().
   */
  setCruiseIntensity(intensity: number): void {
    this.cruiseIntensity = Math.min(1, Math.max(0, intensity));
  }

  /** Build the deterministic shake offset for this frame. */
  private updateShake(thrustFrac: number, speed01: number, boost01: number, t: number): void {
    const amp =
      this.tuning.thrustShake * thrustFrac +
      this.tuning.boostShake * boost01 +
      this.tuning.speedShake * speed01;
    const combatAmp = this.tuning.combatShake * this.impulse;
    const nx = noise(t, 37, 91);
    const ny = noise(t, 53, 113);
    const nz = noise(t, 67, 131);
    const cx = noise(t, 211, 307);
    const cy = noise(t, 263, 359);
    this.shake.pitch = nx * amp + cx * combatAmp;
    this.shake.yaw = ny * amp + cy * combatAmp;
    this.shake.roll = nz * amp * 0.5 + cx * combatAmp * 0.75;
    // Position nudge, hard-clamped under the floating-origin ceiling (AAA-17).
    this.shake.pos.set(
      nx * amp + cx * combatAmp * 0.2,
      ny * amp + cy * combatAmp * 0.2,
      nz * amp,
    ).multiplyScalar(this.tuning.shakePosKm);
    if (this.shake.pos.length() > SHAKE_POS_MAX_KM) this.shake.pos.setLength(SHAKE_POS_MAX_KM);
  }

  /** Apply the smoothed FOV to the camera, updating the projection only on change. */
  applyFov(camera: THREE.PerspectiveCamera): void {
    if (Math.abs(camera.fov - this.fov) < FOV_EPS) return;
    camera.fov = this.fov;
    camera.updateProjectionMatrix();
  }
}
